/**
 * useSeoMeta — fetches public SEO settings and injects <title> + <meta> + JSON-LD
 * into the document head at runtime. Cleans up on unmount so SPA navigation works.
 *
 * Crawlers (Googlebot, Bingbot, etc.) render JS since 2019, so dynamic injection is
 * indexable. For pre-render snapshots, configure a service like prerender.io later.
 */
import { useEffect } from "react";

const API = "/api";
const MARK = "data-gow-seo";

function setMeta(attr, key, value) {
  if (!value) return null;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return el;
}

function setLink(rel, href) {
  if (!href) return null;
  const selector = `link[rel="${rel}"][${MARK}]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function setJsonLd(id, payload) {
  if (!payload) return null;
  const selector = `script[type="application/ld+json"][data-gow-id="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-gow-id", id);
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
  return el;
}

function applySeo(s) {
  if (!s) return;
  // Title
  if (s.site_title) document.title = s.site_title;

  // Standard meta
  setMeta("name", "description", s.site_description);
  setMeta("name", "keywords", s.keywords);
  setMeta("name", "robots", `${s.robots_index ? "index" : "noindex"},${s.robots_follow ? "follow" : "nofollow"}`);

  // Canonical
  if (s.canonical_url) setLink("canonical", s.canonical_url);

  // Open Graph
  setMeta("property", "og:type", "website");
  setMeta("property", "og:site_name", s.business_name || s.site_title || "");
  setMeta("property", "og:title", s.og_title || s.site_title);
  setMeta("property", "og:description", s.og_description || s.site_description);
  setMeta("property", "og:url", s.canonical_url || window.location.href);
  if (s.og_image) {
    setMeta("property", "og:image", s.og_image);
    setMeta("property", "og:image:alt", s.og_image_alt || "");
  }

  // Twitter
  setMeta("name", "twitter:card", s.twitter_card || "summary_large_image");
  setMeta("name", "twitter:title", s.og_title || s.site_title);
  setMeta("name", "twitter:description", s.og_description || s.site_description);
  if (s.og_image) setMeta("name", "twitter:image", s.og_image);
  if (s.twitter_handle) setMeta("name", "twitter:site", s.twitter_handle);

  // LocalBusiness JSON-LD
  if (s.business_name) {
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: s.business_name,
      description: s.site_description,
      url: s.canonical_url || window.location.origin,
      telephone: s.business_phone || undefined,
      address: s.business_address
        ? { "@type": "PostalAddress", streetAddress: s.business_address, addressRegion: s.business_region || "GA", addressCountry: "US" }
        : undefined,
      areaServed: s.business_region || undefined,
      image: s.og_image || undefined,
    };
    // Remove undefined values
    Object.keys(ld).forEach((k) => ld[k] === undefined && delete ld[k]);
    setJsonLd("local-business", ld);
  }
}

export default function useSeoMeta(overrides = null) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/public/seo`, { credentials: "omit" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        applySeo({ ...data, ...(overrides || {}) });
      } catch {
        // silent — SEO is a nice-to-have, never break the UI
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(overrides || {})]);
}
